import { useCallback, useMemo } from 'react';
import { makeStyles, Box, IconButton } from '@material-ui/core';
import { useSelector } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import useFetchList from 'hooks/useFetchList';
import QhsTable from 'components/Shared/Table/Table';
import { useGenerateColumns } from 'components/Shared/Table/helpers';
import FILTER_TYPES from 'components/Shared/Table/widgets/Filters/constants';
import { TABLE_KEYS } from '../../../../../redux_store';

const useStyles = makeStyles(theme => ({
  tableWrapper: {
    flex: 1,
    width: '100%',
    minWidth: 400,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    color: theme.palette.success.main,
    '&:hover': {
      backgroundColor: 'rgba(76, 175, 80, 0.04)',
    },
  },
  selectedButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.04)',
    },
  },
  existingButton: {
    color: theme.palette.grey[400],
    '&:hover': {
      backgroundColor: 'transparent',
      cursor: 'not-allowed',
    },
  },
}));

// Define columns for the accounts table
const ACCOUNT_SELECTION_COLUMNS = [
  {
    size: 300,
    header: 'Account Name',
    accessorKey: 'businessName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
    includeCurrentAccount: true,
  },
  {
    size: 200,
    header: 'Custom ID',
    accessorKey: 'customIdentifier',
    filter: {},
    isDefaultCell: true,
  },
];

const AccountsTable = ({ accountUuid, selectedAccounts, onSelectionChange, isLoading = false }) => {
  const classes = useStyles();
  const {
    en: { titles },
  } = useSelector(state => state.i18n);
  const { userAccounts } = useSelector(state => state.userAccountManagement);
  const tableKey = TABLE_KEYS.accountsTable;

  // Generate columns with proper configuration
  const columns = useGenerateColumns(ACCOUNT_SELECTION_COLUMNS, tableKey, {}, {}).columns;

  // Fetch accounts data from lookups endpoint
  const {
    items,
    totalCount,
    loading: dataLoading,
    onLoadMoreRows,
    onSort,
    onFilter,
    sort,
    filter,
    fetchError,
  } = useFetchList({
    baseEndpoint: `/accounts`,
    baseParams: { fields: 'businessName,customIdentifier', includeCurrentAccount: true },
    canLoad: !!accountUuid,
    tableKey,
    ignorePrefilters: true,
  });

  // Convert items to table data format
  const tableData = useMemo(() => {
    if (!items || items.length === 0) return [];

    const data = items.map((item, index) => ({
      uuid: item.uuid || `${item.businessName}_${index}`,
      businessName: item.businessName || item || '',
      customIdentifier: item.customIdentifier || '',
      ...item,
    }));

    return data;
  }, [items]);

  // Check if account is already selected
  const isAccountSelected = useCallback(
    account => {
      return selectedAccounts.some(
        selected => selected.uuid === account.uuid || selected.businessName === account.businessName
      );
    },
    [selectedAccounts]
  );

  // Check if account already exists for this user
  const isAccountExisting = useCallback(
    account => {
      return userAccounts.some(
        existing =>
          existing.uuid === account.uuid ||
          existing.accountBusinessName === account.businessName ||
          existing.name === account.businessName
      );
    },
    [userAccounts]
  );

  // Handle account toggle
  const handleAccountToggle = useCallback(
    account => {
      // Don't allow toggling existing accounts
      if (isAccountExisting(account)) {
        return;
      }

      const isSelected = isAccountSelected(account);
      let newSelection;

      if (isSelected) {
        // Remove from selection
        newSelection = selectedAccounts.filter(
          selected =>
            selected.uuid !== account.uuid && selected.businessName !== account.businessName
        );
      } else {
        // Add to selection
        newSelection = [...selectedAccounts, account];
      }

      onSelectionChange(newSelection);
    },
    [selectedAccounts, onSelectionChange, isAccountSelected, isAccountExisting]
  );

  // Render row actions (Add/Selected button)
  const renderRowActions = useCallback(
    ({ row }) => {
      const account = row.original;
      const isSelected = isAccountSelected(account);
      const isExisting = isAccountExisting(account);

      // For existing accounts, show disabled check icon
      if (isExisting) {
        return (
          <div className={classes.buttonContainer}>
            <IconButton
              className={classes.existingButton}
              disabled={true}
              title="Account already exists for this user"
              size="small"
            >
              <CheckIcon />
            </IconButton>
          </div>
        );
      }

      return (
        <div className={classes.buttonContainer}>
          <IconButton
            className={isSelected ? classes.selectedButton : classes.addButton}
            onClick={() => handleAccountToggle(account)}
            title={isSelected ? 'Remove from selection' : 'Add to selection'}
            size="small"
          >
            {isSelected ? <CheckIcon /> : <AddIcon />}
          </IconButton>
        </div>
      );
    },
    [classes, isAccountSelected, isAccountExisting, handleAccountToggle]
  );

  return (
    <Box className={classes.tableWrapper}>
      <QhsTable
        columns={columns}
        data={tableData || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading || isLoading || !accountUuid}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={onFilter}
        tableKey={tableKey}
        renderRowActions={renderRowActions}
        rowActionsTitleSize={20}
        getRowId={row => row.uuid}
        fetchError={fetchError}
        muiTableContainerProps={{
          sx: {
            maxHeight: '450px',
          },
        }}
      />
    </Box>
  );
};

export default AccountsTable;
